import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import { CountryCode, TangoTeamProduct } from './types';
import { getProductPricingAndNutrientInfo } from '../api/getProductPricingAndNutrientInfo';
import { Dropdown } from '../component/Dropdown';
import { ProductSearchBar } from '../component/ProductSearchBar';
import { TableProductInfo } from '../component/TableProductInfo';
import { countryOptions } from '../dropdownOptions';
import { useProductInfoUrlState } from '../hooks/useProductInfoUrlState';
import { AncillaryProductInfoHeaders } from '../tableHeaders';
import {
  BOTTOM_TOAST,
  createRowClickHandler,
  formatPriceWithCurrency,
  generateErrorMsg,
  getDefaultPricingModelOption,
  getPricingModelOptionsForCountry,
} from '../utils';

export const AncillaryProductInfo = () => {
  const {
    localSearchText,
    setLocalSearchText,
    currentSearchText,
    setCurrentSearchText,
    selectedCountry,
    setSelectedCountry,
    selectedPricingModel,
    setSelectedPricingModel,
  } = useProductInfoUrlState();

  const [ancillaryProduct, setAncillaryProduct] = useState<TangoTeamProduct[]>([]);
  const [loading, setLoading] = useState(true);

  const [highlightedRows, setHighlightedRows] = useState<Set<number>>(new Set());
  const [lastSelectedRow, setLastSelectedRow] = useState<number | null>(null);

  useEffect(() => {
    getProductPricingAndNutrientInfo({ meal_type: 'ANCILLARY' })
      .then((res) => {
        setAncillaryProduct(res.data.reverse());
      })
      .catch((err) => {
        toast.error(generateErrorMsg(err), BOTTOM_TOAST);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const pricingModelOptions = useMemo(
    () => getPricingModelOptionsForCountry(ancillaryProduct, selectedCountry.value),
    [ancillaryProduct, selectedCountry]
  );

  // Keep the pricing model valid for the selected country
  useEffect(() => {
    if (!pricingModelOptions.length) return;
    if (!pricingModelOptions.some((option) => option.value === selectedPricingModel.value)) {
      setSelectedPricingModel(getDefaultPricingModelOption(pricingModelOptions));
    }
  }, [pricingModelOptions, selectedPricingModel, setSelectedPricingModel]);

  const filteredProducts = useMemo(
    () =>
      ancillaryProduct.filter(
        (product: TangoTeamProduct) =>
          currentSearchText === '' || product.code.includes(currentSearchText.toLocaleUpperCase())
      ),
    [ancillaryProduct, currentSearchText]
  );

  const firstHeaderGroups = [
    { label: '', colSpan: 5, className: 'bg-slate50' },
    { label: 'Pricing', colSpan: 1, className: 'bg-green-200 font-semibold' },
    { label: '', colSpan: 1, className: 'bg-purple-200 font-semibold' },
  ];

  const handleRowClick = createRowClickHandler(
    setHighlightedRows,
    setLastSelectedRow,
    lastSelectedRow,
    highlightedRows
  );

  const transformProductToTableRow = (product: TangoTeamProduct) => {
    const prices = product?.prices?.[selectedPricingModel.value];
    const currency = prices?.currency ? `${prices.currency} $` : '';

    return {
      Code: product.code,
      Description: product.description_bark,
      Type: product.type,
      Recurring: product.recurring.toString(),
      Status: selectedCountry.value === CountryCode.CA ? product.status_ca : product.status_us,
      'Price (per Product Code)': formatPriceWithCurrency(currency, prices?.price),
      'Product contents': product.recipe_data_point,
    };
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-10 flex-shrink-0">
        <h1>Ancillary Product Info</h1>
      </div>

      {!loading && (
        <div className="px-10 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg border">
            <ProductSearchBar
              localSearchText={localSearchText}
              setLocalSearchText={setLocalSearchText}
              setCurrentSearchText={setCurrentSearchText}
            />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end border-b pb-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">Country</label>
                <Dropdown
                  options={countryOptions}
                  selectedOption={selectedCountry}
                  setSelectedOption={setSelectedCountry}
                  defaultText="Select Country"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-2">Pricing Model</label>
                <Dropdown
                  options={pricingModelOptions}
                  selectedOption={selectedPricingModel}
                  setSelectedOption={setSelectedPricingModel}
                  defaultText="Select Pricing Model"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          <span className="ml-2">Loading...</span>
        </div>
      ) : (
        <div className="px-10 flex-1 flex flex-col">
          <div className="flex-1 border border-gray-300 rounded-lg">
            <TableProductInfo
              header={AncillaryProductInfoHeaders}
              data={filteredProducts.map((product: TangoTeamProduct) =>
                transformProductToTableRow(product)
              )}
              topHeaderGroups={firstHeaderGroups}
              highlightedRows={highlightedRows}
              onRowClick={handleRowClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};
